import { Button, Drawer, Skeleton } from "antd";
import { type CSSProperties, useEffect, useState } from "react";
import { IconButton, SvgIcon } from "@/components/icon";
import Logo from "@/components/logo";
import { useSettingActions, useSettings } from "@/store/settingStore";
import AccountDropdown from "../components/account-dropdown";
import SettingButton from "../components/setting-button";
import { themeVars } from "@/theme/theme.css";
import { cn } from "@/utils";
import { rgbAlpha } from "@/utils/theme";
import { ThemeLayout, ThemeMode } from "#/enum";
import { HEADER_HEIGHT, NAV_COLLAPSED_WIDTH, NAV_WIDTH } from "./config";
import NavVertical from "./nav/nav-vertical";
import { ClockCircleOutlined, CrownOutlined, MoonOutlined, SunOutlined } from "@ant-design/icons";
import { FiChevronRight } from "react-icons/fi";
import { useCreditsRemaining, useDaysRemaining, useTrialEndTime, useTotalCreditsAllocated, useTierId, useTier, useIsFetchingCredits, useUserInfo } from "@/store/userStore";
import { RxLightningBolt } from "react-icons/rx";
import { useLocation, useNavigate } from "react-router";
import { useCurrentRouteMeta } from "@/router/hooks";
import { useTranslation } from "react-i18next";
function useCountdown(endTime: Readonly<number | null>) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, (endTime ?? 0) - Date.now()));
  useEffect(() => {
    if (!endTime || endTime <= 0) {
      setTimeLeft(0);
      return;
    }
    setTimeLeft(Math.max(0, endTime - Date.now()));
    const interval = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [endTime]);
  const totalSeconds = Math.floor(timeLeft / 1000);
  const dd = Math.floor(totalSeconds / 86400);
  const hh = Math.floor(totalSeconds % 86400 / 3600);
  const mm = Math.floor(totalSeconds % 3600 / 60);
  const ss = totalSeconds % 60;
  const pad = (n: Readonly<number>) => String(n).padStart(2, "0");
  return {
    formatted: `${pad(dd)}:${pad(hh)}:${pad(mm)}:${pad(ss)}`,
    isExpired: totalSeconds <= 0,
    dd,
    hh,
    mm,
    ss
  };
}
function CustomThemeSwitch({
  isDark,
  onChange
}: Readonly<{
  readonly isDark: boolean;
  readonly onChange: (checked: boolean) => void;
}>) {
  return <button type="button" aria-label="Toggle theme" className={cn("relative flex items-center w-[64px] h-[32px] rounded-full cursor-pointer p-[3px] transition-colors duration-300 mr-4 border-0 outline-none", isDark ? "bg-[#1E293B]" : "bg-[#F1F5F9]")} onClick={() => onChange(!isDark)}>

    <div className="absolute w-full left-0 flex justify-between px-[8px] z-0 pointer-events-none">
      <SunOutlined style={{
        fontSize: 15,
        color: isDark ? "#64748B" : "transparent"
      }} />
      <MoonOutlined style={{
        fontSize: 15,
        color: !isDark ? "#94A3B8" : "transparent"
      }} />
    </div>

    <div className={cn("absolute flex items-center justify-center w-[26px] h-[26px] rounded-full shadow-sm z-10 transition-transform duration-300", isDark ? "translate-x-[32px] bg-[#334155]" : "translate-x-0 bg-white")}>

      {isDark ? <MoonOutlined style={{
        fontSize: 14,
        color: "#E2E8F0"
      }} /> : <div className="relative flex items-center justify-center">
        <span className="absolute -top-[1px] -left-[1px] w-[6px] h-[6px] bg-red-500 rounded-full border border-white" />
        <SunOutlined style={{
          fontSize: 14,
          color: "#F59E0B"
        }} />
      </div>}
    </div>
  </button>;
}
function UserCreditsStatus({
  isFetchingCredits,
  tierId,
  themeMode,
  tier,
  countdown,
  dayRemaining,
  creditsRemaining,
  totalCreditsAllocated
}: Readonly<{
  isFetchingCredits: boolean;
  tierId: number;
  themeMode: ThemeMode;
  tier: string | null;
  countdown: any;
  dayRemaining: number;
  creditsRemaining: number;
  totalCreditsAllocated: number | null;
}>) {
  const navigate = useNavigate();
   const roleId = localStorage.getItem("roleId");

  if (isFetchingCredits) {
    return (
      <div className="flex items-center gap-3">
        <Skeleton.Button active size="small" shape="round" style={{ width: 100 }} />
        <Skeleton.Button active size="small" shape="round" style={{ width: 90 }} />
        {tierId === 1 && <Skeleton.Button active size="small" shape="round" style={{ width: 100 }} />}
        <Skeleton.Button active size="small" shape="round" style={{ width: 110 }} />
      </div>
    );
  }

  const isDark = themeMode === ThemeMode.Dark;
  const creditsText = creditsRemaining <= 0 ? "0" : Math.round(creditsRemaining);
  const totalAllocated = totalCreditsAllocated !== null ? Math.round(Number(totalCreditsAllocated)) : "30";
  const creditLabel = tierId === 1 ? "credits" : "monthly credits";

  return (
    <>
      {/* Upgrade Plan Button */}
      {
        ["1","5","0"].includes(roleId) && (
          <button
        type="button"
        className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold text-white bg-[#C41230] hover:bg-[#a80e28] transition-all cursor-pointer border-none shadow-sm"
        onClick={() => navigate("/dashboard/upgrade-plan")}
      >
        <CrownOutlined style={{ fontSize: 13 }} />
        <span>Upgrade plan</span>
      </button>
        )
      }

      {/* Free Trial Status */}
      <div
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${["2", "3", "4","6"].includes(String(roleId)) ? "" : "cursor-pointer"}`}
        style={{
          borderColor: isDark ? "rgba(196,18,48,0.3)" : "rgba(196,18,48,0.2)",
          backgroundColor: isDark ? "rgba(196,18,48,0.1)" : "rgba(196,18,48,0.05)"
        }}
        onClick={() => {
          if (!["2", "3", "4","6"].includes(String(roleId))) {
            navigate("/dashboard/upgrade-plan");
          }
        }}
      >
        <RxLightningBolt style={{ color: "#C41230" }} />
        <span className="text-xs font-semibold text-[#C41230] capitalize">
          {String(roleId) === "4" ? "Enterprise" : (tier ? tier : "Free trial")}
        </span>
      </div>

      {/* Days Remaining */}
      {tierId === 1 && (
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
          style={{
            borderColor: isDark ? "rgba(254, 243, 199, 0.3)" : "rgba(254, 243, 199, 1)",
            backgroundColor: isDark ? "rgba(255, 251, 235, 0.2)" : "rgba(255, 251, 235, 1)"
          }}
        >
          <ClockCircleOutlined style={{ color: "rgba(245, 158, 11, 1)", fontSize: 13 }} />
          <span className="text-xs font-bold text-[#B45309]">
            {countdown.isExpired ? "0" : dayRemaining + 1} days left
          </span>
        </div>
      )}

      {/* Credits Used */}
     {
      roleId !== "4" && (
         <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
        style={{
          borderColor: isDark ? "rgba(224, 231, 255, 0.3)" : "rgba(224, 231, 255, 1)",
          backgroundColor: isDark ? "rgba(238, 242, 255, 0.1)" : "rgba(238, 242, 255, 1)"
        }}
      >
        <svg width="15" height="19" viewBox="0 0 15 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 14C9 14 8.6875 14 8.0625 14C7.4375 14 6.6875 13.5625 5.8125 12.6875C4.9375 11.8125 4.5 10.75 4.5 9.5C4.5 8.25 4.9375 7.1875 5.8125 6.3125C6.6875 5.4375 7.75 5 9 5C10.25 5 11.3125 5.4375 12.1875 6.3125C13.0625 7.1875 13.5 8.25 13.5 9.5C13.5 10.75 13.0625 11.8125 12.1875 12.6875C11.3125 13.5625 10.25 14 9 14ZM5.25 13.9375C4.15625 13.7604 3.25781 13.2526 2.55469 12.4141C1.85156 11.5755 1.5 10.6042 1.5 9.5C1.5 8.39583 1.85417 7.42708 2.5625 6.59375C3.27083 5.76042 4.16667 5.25 5.25 5.0625V6.21875C4.47917 6.39583 3.84896 6.79167 3.35938 7.40625C2.86979 8.02083 2.625 8.71875 2.625 9.5C2.625 10.2917 2.86979 10.9922 3.35938 11.6016C3.84896 12.2109 4.47917 12.6042 5.25 12.7812V13.9375ZM9 12.875C9 12.875 9.23438 12.875 9.70312 12.875C10.1719 12.875 10.7344 12.5469 11.3906 11.8906C12.0469 11.2344 12.375 10.4375 12.375 9.5C12.375 8.5625 12.0469 7.76562 11.3906 7.10938C10.7344 6.45312 9.9375 6.125 9 6.125C8.0625 6.125 7.26562 6.45312 6.60938 7.10938C5.95312 7.76562 5.625 8.5625 5.625 9.5C5.625 10.4375 5.95312 11.2344 6.60938 11.8906C7.26562 12.5469 8.0625 12.875 9 12.875Z" fill="#818CF8" />
        </svg>
        <span className={`text-xs font-bold ${isDark ? 'text-[#818CF8]' : 'text-[#4F46E5]'}`}>
          {creditsText}/{totalAllocated} {creditLabel}
        </span>
      </div>
      )
     }
    </>
  );
}

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const settings = useSettings();
  const {
    themeLayout,
    themeMode
  } = settings;
  const {
    setSettings
  } = useSettingActions();
  const role_id = localStorage.getItem("roleId");
  const userInfo = useUserInfo() as any;
  const isSuperAdmin = role_id === "1" || userInfo?.role_id === 1 || String(userInfo?.role_id) === "1";
  const dayRemaining = useDaysRemaining();
  const trialEndTime = useTrialEndTime();
  const creditsRemaining = useCreditsRemaining();
  const totalCreditsAllocated = useTotalCreditsAllocated();
  const tierId = useTierId();
  const tier = useTier();
  const isFetchingCredits = useIsFetchingCredits();
  const countdown = useCountdown(trialEndTime);
  const location = useLocation();
  const currentRouteMeta = useCurrentRouteMeta();
  const {
    t
  } = useTranslation();
  const blockedPaths = ["redirecting-to-payment-partner", "waiting-for-payment-confirmation", "payment/success", "payment/cancelled", "payment-error", "transfer-dashboards-success"];
  const isBlocked = blockedPaths.some(p => location.pathname.includes(p));
  const headerStyle: CSSProperties = {
    borderBottom: themeLayout === ThemeLayout.Horizontal ? `1px dashed ${rgbAlpha(themeVars.colors.palette.gray["500Channel"], 0.2)}` : "",
    backgroundColor: rgbAlpha(themeVars.colors.background.defaultChannel, 0.9),
    width: "100%",
    pointerEvents: isBlocked ? "none" : "auto"
  };
  return <>
    <header className={cn(themeLayout === ThemeLayout.Horizontal ? "relative" : "sticky top-0 right-0 left-auto")} style={headerStyle}>

      <div className={`flex flex-grow items-center justify-between px-4 text-gray backdrop-blur xl:px-4 2xl:px-4 border-b-[1px] ${themeMode === ThemeMode.Dark ? 'border-gray-800' : 'border-[#E2E8F0]'}`} style={{
        height: HEADER_HEIGHT,
        transition: "height 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms"
      }}>

        <div className="flex items-baseline">
          {themeLayout !== ThemeLayout.Horizontal ? <IconButton onClick={() => setDrawerOpen(true)} className="h-10 w-10 md:hidden">
            <SvgIcon icon="ic-menu" size="24" />
          </IconButton> : <Logo />}
          {/* <div className="ml-4 hidden md:block">{breadCrumb ? <BreadCrumb /> : null}</div> */}
          <div className="flex items-center gap-2">
            <span className="font-semibold text-primary">VantageAI</span>
            <FiChevronRight className="text-gray-500" />
            <span className={`font-semibold ${themeMode === ThemeMode.Dark ? 'text-white' : 'text-black'}`}>{currentRouteMeta?.label ? t(currentRouteMeta.label) : location?.pathname}</span>
          </div>
        </div>

        <div className="flex items-center">
          {!isSuperAdmin && (
            <div className="flex items-center gap-3 mr-4">
              <UserCreditsStatus
                isFetchingCredits={isFetchingCredits}
                tierId={tierId}
                themeMode={themeMode}
                tier={tier}
                countdown={countdown}
                dayRemaining={dayRemaining}
                creditsRemaining={creditsRemaining}
                totalCreditsAllocated={totalCreditsAllocated}
              />
            </div>
          )}
          {/* <SearchBar /> */}
          {/* <LocalePicker />
                            <IconButton onClick={() => window.open("https://github.com/d3george/slash-admin")}>
                            <Iconify icon="mdi:github" size={24} />
                            </IconButton>
                            <IconButton onClick={() => window.open("https://discord.gg/fXemAXVNDa")}>
                            <Iconify icon="carbon:logo-discord" size={24} />
                            </IconButton> */}
          {/* <NoticeButton /> */}
          <CustomThemeSwitch isDark={themeMode === ThemeMode.Dark} onChange={checked => setSettings({
            ...settings,
            themeMode: checked ? ThemeMode.Dark : ThemeMode.Light
          })} />
          <SettingButton />
          <AccountDropdown isBlocked={isBlocked} isExpired={countdown.isExpired} />
        </div>
      </div>
    </header>
    <Drawer placement="left" onClose={() => setDrawerOpen(false)} open={drawerOpen} closeIcon={false} width={themeLayout === ThemeLayout.Mini ? NAV_COLLAPSED_WIDTH : NAV_WIDTH}>

      <NavVertical closeSideBarDrawer={() => setDrawerOpen(false)} />
    </Drawer>
  </>;
}